import React from "react";

function formatStepTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function buildTrackerSteps(status, { requestedAt, processedAt }) {
  const s = String(status || "").toUpperCase();
  const rejected = s === "CANCELLED" || s === "REJECTED";
  const fulfilled = s === "COMPLETED" || s === "PARTIALLY_COMPLETED";
  const pending = s === "PENDING";

  const requestedTime = formatStepTime(requestedAt);
  const processedTime = formatStepTime(processedAt);

  const step1 = {
    key: "submitted",
    title: "Submitted",
    detail: requestedTime ? `Submitted on ${requestedTime}` : "Your redemption request was sent to the platform.",
    state: "complete",
  };

  let step2;
  if (rejected) {
    step2 = {
      key: "review",
      title: s === "CANCELLED" ? "Request cancelled" : "Not approved",
      detail:
        s === "CANCELLED"
          ? "You cancelled this request. Locked shares were released."
          : "This request was not approved. Locked shares were released.",
      state: "failed",
    };
  } else if (fulfilled) {
    step2 = {
      key: "review",
      title: "Approved",
      detail: "Admin approved your redemption at the NAV locked on submission.",
      state: "complete",
    };
  } else {
    step2 = {
      key: "review",
      title: "Under review",
      detail: "Platform admin is reviewing your request. Those shares stay locked until a decision.",
      state: pending ? "current" : "upcoming",
    };
  }

  let step3;
  if (rejected) {
    step3 = {
      key: "settled",
      title: "Settlement",
      detail: "No payout — this request did not settle.",
      state: "skipped",
    };
  } else if (fulfilled) {
    step3 = {
      key: "settled",
      title: "Settled",
      detail: processedTime
        ? `Settled on ${processedTime}`
        : "Cash settlement recorded to your wallet.",
      state: "complete",
    };
  } else {
    step3 = {
      key: "settled",
      title: "Settlement",
      detail: "After approval, payout is processed to your wallet.",
      state: "upcoming",
    };
  }

  return [step1, step2, step3];
}

function StepMarker({ state }) {
  if (state === "complete") {
    return (
      <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
        <path d="M6.2 11.4 3.4 8.6l-.9.9 3.7 3.7 8.5-8.5-.9-.9z" />
      </svg>
    );
  }
  if (state === "failed") {
    return (
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
        <path
          d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    );
  }
  if (state === "current") {
    return <span className="investor-transfer-tracker__pulse" />;
  }
  if (state === "skipped") {
    return <span className="investor-transfer-tracker__dash" />;
  }
  return <span className="investor-transfer-tracker__ring" />;
}

export default function RedemptionProgressSteps({ status, requestedAt, processedAt }) {
  const steps = buildTrackerSteps(status, { requestedAt, processedAt });

  return (
    <div className="investor-transfer-tracker" aria-label="Redemption tracking">
      <ol className="investor-transfer-tracker__timeline">
        {steps.map((step, index) => (
          <li key={step.key} className={`investor-transfer-tracker__step is-${step.state}`}>
            <span className="investor-transfer-tracker__marker" aria-hidden="true">
              <StepMarker state={step.state} />
            </span>
            <div className="investor-transfer-tracker__content">
              <span className="investor-transfer-tracker__title">{step.title}</span>
              <span className="investor-transfer-tracker__detail">{step.detail}</span>
            </div>
            {index < steps.length - 1 ? <span className="investor-transfer-tracker__rail" aria-hidden="true" /> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
